import { Component } from '@angular/core';
import { Router } from '@angular/router';

import { SplashScreen } from '@capacitor/splash-screen';
import { Preferences } from '@capacitor/preferences';

import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

import { HistoryHelperService } from './utils/history-helper.service';
import { DashboardService } from './nenomed-main/services/dashboard.service';
import { NotebookDto, WorkSpaceDto } from './nenomed-main/models/models';
import { AuthService } from './nenomed-main/services/auth.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AlertController, ToastController } from '@ionic/angular';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: [
    './side-menu/styles/side-menu.scss',
    './side-menu/styles/side-menu.shell.scss',
    './side-menu/styles/side-menu.responsive.scss'
  ]
})
export class AppComponent {
  appPages = [
    {
      title: 'Categories',
      url: '/app/categories',
      ionicIcon: 'list-outline'
    },
    {
      title: 'Profile',
      url: '/app/user',
      ionicIcon: 'person-outline'
    },
    {
      title: 'Contact Card',
      url: '/contact-card',
      customIcon: './assets/custom-icons/side-menu/contact-card.svg'
    },
    {
      title: 'Notifications',
      url: '/app/notifications',
      ionicIcon: 'notifications-outline'
    },
    {
      title: 'Agenda',
      url: '/app/agenda',
      ionicIcon: 'calendar-outline'
    }
  ];

  accountPages = [
    {
      title: 'Salir',
      url: '/auth/login',
      ionicIcon: 'log-in-outline'
    },
    //{
    //   title: 'Sign Up',
    //   url: '/auth/signup',
    //   ionicIcon: 'person-add-outline'
    // },
    {
      title: 'Perfil',
      url: '/',
      ionicIcon: 'rocket-outline'
    }
    //,
    // {
    //   title: '404 page',
    //   url: '/page-not-found',
    //   ionicIcon: 'alert-circle-outline'
    // }
  ];

  currentWorkspace : any;
  textDir = 'ltr';
  // Inject HistoryHelperService in the app.components.ts so its available app-wide
  constructor(
    private authService: AuthService,
    public translate: TranslateService,
    public historyHelper: HistoryHelperService,
    private router: Router,
    private dashboardService: DashboardService,
    private alertController: AlertController,
    private toastCtrl: ToastController,
    private swUpdate: SwUpdate
  ) {
    this.initializeApp();
    this.setLanguage();
    this.setupUpdateCheck();

    
  }

  compareFn(e1: WorkSpaceDto, e2: WorkSpaceDto): boolean {
    return e1 && e2 ? e1.id == e2.id : e1 == e2;
  }

  getCurrentWorkspace()
  {
    return this.dashboardService.getCurrentWorkspace();
  }
  
  onWorkspaceChange($event)
  {
    this.currentWorkspace = $event.detail.value;
    this.dashboardService.setCurrentWorkspace(this.currentWorkspace);
  }

  async initializeApp() {
    try {

   
      await StatusBar.setStyle({ style: Style.Light});
      await StatusBar.setBackgroundColor({ color: '#00000000' });
      await SplashScreen.hide();
      this.currentWorkspace = this.dashboardService.getCurrentWorkspace();

    } catch (err) {
      console.log('This is normal in a browser', err);
    }
  }

  public setLanguage(): void {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('es');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this.translate.use('es');

    // this is to determine the text direction depending on the selected language
    // for the purpose of this example we determine that only arabic and hebrew are RTL.
    // this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
    //   this.textDir = (event.lang === 'ar' || event.lang === 'iw') ? 'rtl' : 'ltr';
    // });
  }

  public openTutorial(): void {
    // save key to mark the walkthrough as NOT visited because the user wants to check it out
    Preferences.set({
      key: 'visitedWalkthrough',
      value: 'false'
    });
    this.router.navigateByUrl('walkthrough');
  }

  getWorkspaces():WorkSpaceDto[]
  {
    return this.dashboardService.getCurrentWorkspaces();
  }  

  // getBooks():NotebookDto[]
  // {
  //   return this.dashboardService.getCurrentNotebooks();
  // }

  getBooks():NotebookDto[]
  {
    return this.dashboardService.getCurrentNotebooks().filter(n=>n.planId==null);
  }

  getPlans():NotebookDto[]
  {
    return this.dashboardService.getCurrentNotebooks().filter(n=>n.planId!=null);
  }

  getMe():any{
    return this.authService.meData;
  }

  getPicture():string{
   if(this.authService.meData?.picture!=null)
      {
        return this.authService.meData?.picture;
      }
      return "";
  }

  getPlanIcon():string{
    if(this.authService.meData?.image!=null)
      {
        return this.authService.meData?.planIconUrl;
      }
      return "";
    
  }
  
  selectBook(book: NotebookDto)
  {
    this.dashboardService.setCurrentNotebook(book);
  }

  private setupUpdateCheck() {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
    ).subscribe(async () => {
      await this.swUpdate.activateUpdate();
      const toast = await this.toastCtrl.create({
        message: 'Nueva versión disponible',
        position: 'top',
        color: 'dark',
        buttons: [{ text: 'Actualizar ahora', handler: () => window.location.reload() }],
        duration: 0
      });
      await toast.present();
    });

    // Verificar al inicio
    this.swUpdate.checkForUpdate();

    // Verificar cada 5 minutos
    interval(5 * 60 * 1000).subscribe(() => this.swUpdate.checkForUpdate());

    // Verificar al recuperar foco
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.swUpdate.checkForUpdate();
    });
  }

  async confirmLogout() {
    const alert = await this.alertController.create({
      header: 'Confirmar',
      message: '¿Seguro que deseas cerrar sesión?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel'
        },
        {
          text: 'Salir',
          handler: () => {
            // Aquí puedes limpiar datos si lo necesitas
            this.authService.SignOut();
          }
        }
      ]
    });
    await alert.present();
  }
}
